import { ReactNode, useCallback, useState } from 'react';
import MainPageLayout from 'layouts/MainPageLayout';
import { FormattedMessage } from 'react-intl';
import { Grid } from '@mui/material';
import FileTypeSwitcher from './Share/FileTypeSwitcher';
import ShareExplorer from './Share/ShareExplorer';
import useStyles from './Share.styles';
import { ShareContextProvider } from './Share/ShareContext';
import { FolderType , SharedFolder, SharedDocument } from 'types';
import { useMutation, useQuery } from '@apollo/client';
import {
  DOCUMENT_FRAGMENT,
} from 'graphql/document';
import {
  GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
  GetVisibleFoldersFromFolderOutput,
} from 'graphql/folder';
import { useSelectedLinkContext } from 'contexts';

import {
  getAcceptedFiles,
  getSortedFolders,
  sendDroppedFiles,
} from './Share/Helpers';
import ExplorerHeader from './Share/ExplorerHeader';
import { useDropzone } from 'react-dropzone';
import { useUploadFileToS3 } from 'hooks/useUploadFileToS3';
import {
  ADD_DOCUMENT_MUTATION,
  AddDocumentOutput,
} from 'graphql/document/DocumentMutations';
import DocumentOperationResultsModal from './Share/DocumentOperationResultsModal';
import IndeterminateLoading from 'components/feedbacks/IndeterminateLoading';
import { DeleteModeContextProvider } from './Share/ShareExplorer/FolderContentExplorer/DeleteMode/DeleteModeContext';
import ExplorerToolbar from './Share/ExplorerToolbar';
import { ResultItemProps } from './Share/DocumentOperationResultsModal/ResultItem';
import { MAX_UPLOADED_DOCUMENT_SIZE } from 'constants/env';
import { emptyFunction } from 'utils/functions';
import {useSnackbar} from 'hooks';

const Share = (): JSX.Element => {
  const classes = useStyles();
  const { selectedLink } = useSelectedLinkContext();

  const [selectedFileType, setSelectedFileType] =
    useState<FolderType>(
      FolderType.PICTURE_AND_VIDEO,
    );

  const [loadingMessage, setLoadingMessage] = useState<ReactNode>();

  const [selectedFolder, setSelectedFolder] = useState<SharedFolder>();

  const [documentOperationResults, setDocumentOperationResults] = useState<
    ResultItemProps[]
  >([]);

  const [isDeleteMode, setIsDeleteMode] = useState<boolean>(false);

  const [documentsToDelete, setDocumentsToDelete] = useState<
    SharedDocument[]
  >([]);

  const { errorSnackbar, successSnackbar } = useSnackbar();

  const { uploadFileToS3 } = useUploadFileToS3({
    handleProgress: emptyFunction,
    errorSnackbar,
  });

  const [addDocument] = useMutation<AddDocumentOutput>(
    ADD_DOCUMENT_MUTATION,
    {
      update(cache, { data: updatedData }) {
        const document = updatedData?.addDocument.document;

        if (document) {
          cache.modify({
            fields: {
              visibleDocumentsFromFolder(visibleDocumentsFromFolderResponse) {
                const newDocumentRef = cache.writeFragment({
                  data: document,
                  fragment: DOCUMENT_FRAGMENT,
                  fragmentName: 'documentFields',
                });
                return {
                  ...visibleDocumentsFromFolderResponse,
                  documents: [
                    ...visibleDocumentsFromFolderResponse.documents,
                    newDocumentRef,
                  ],
                };
              },
            },
          });
        }
      },
    },
  );

  const handleSendDroppedFiles = sendDroppedFiles({
    selectedFolder,
    selectedLink,
    addDocument,
    setLoadingMessage,
    setDocumentOperationResults,
    successSnackbar,
    uploadFileToS3,
  });

  const handleFileDrop = useCallback(handleSendDroppedFiles, [
    handleSendDroppedFiles,
    selectedFolder?.key,
    selectedLink?.channelKey,
    addDocument,
    setLoadingMessage,
    setDocumentOperationResults,
    errorSnackbar,
    successSnackbar,
    uploadFileToS3,
  ]);

  const dropzoneState = useDropzone({
    accept: getAcceptedFiles(selectedFileType),
    maxSize: MAX_UPLOADED_DOCUMENT_SIZE,
    onDrop: handleFileDrop,
  });

  const { data } = useQuery<GetVisibleFoldersFromFolderOutput>(
    GET_VISIBLE_FOLDERS_BY_FOLDER_TYPE_QUERY,
    {
      variables: {
        getVisibleFoldersByFolderTypeInput: {
          channel: selectedLink?.channelKey,
          folderType: selectedFileType,
        },
      },
    },
  );

  const sortedFolders = getSortedFolders(
    data?.visibleFoldersFromFolderType.folders,
  );

  const explorerHeaderXsValue = isDeleteMode ? 0 : 9;
  const explorerToolbarXsValue = isDeleteMode ? 12 : 3;

  return (
    <ShareContextProvider
      documentOperationResults={documentOperationResults}
      dropzoneState={dropzoneState}
      folders={sortedFolders}
      loadingMessage={loadingMessage}
      selectedFileType={selectedFileType}
      selectedFolder={selectedFolder}
      setDocumentOperationResults={setDocumentOperationResults}
      setLoadingMessage={setLoadingMessage}
      setSelectedFileType={setSelectedFileType}
      setSelectedFolder={setSelectedFolder}
    >
      <DeleteModeContextProvider
        documentsToDelete={documentsToDelete}
        isDeleteMode={isDeleteMode}
        setDocumentsToDelete={setDocumentsToDelete}
        setIsDeleteMode={setIsDeleteMode}
      >
        <MainPageLayout title={<FormattedMessage id="share.description" />}>
          <IndeterminateLoading label={loadingMessage} />

          <Grid className={classes.container} container>
            <Grid item xs={12}>
              <FileTypeSwitcher />
            </Grid>

            <Grid
              className={classes.explorerHeader}
              item
              md={6}
              xs={explorerHeaderXsValue}
            >
              <ExplorerHeader />
            </Grid>

            <Grid
              className={classes.explorerToolbarContainer}
              item
              md={6}
              xs={explorerToolbarXsValue}
            >
              <ExplorerToolbar />
            </Grid>

            <Grid className={classes.explorerContainer} item xs={12}>
              <ShareExplorer />
            </Grid>
          </Grid>

          <DocumentOperationResultsModal />
        </MainPageLayout>
      </DeleteModeContextProvider>
    </ShareContextProvider>
  );
};

export default Share;
